import { Button, Icon, LoadingDots } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import LinearLoader from '@trustoo/components/ui/LinearLoader'
import { fetchProvider } from 'actions/providers'
import clsx from 'clsx'
import { useAppDispatch } from 'hooks/hooks'
import { hasAccess, isSuperUser } from 'misc/user'
import { type ReactNode, useState } from 'react'
import type { GoLiveRequirement, GoLiveRequirementOwner, GoLiveRequirementsResponse } from './types'
import {
    enableProfile,
    recalculateScore,
    toEnableProfileError,
    toRecalculateScoreError,
    useGoLiveRequirements,
} from './useGoLiveRequirements'

interface GoLiveRequirementsModalProps {
    providerId: number
}

type Tone = 'success' | 'warning' | 'error'

/** This app's Tailwind theme has no `red` or `amber` ramp; severity lives on these tokens. */
const TONE_CLASSES: Record<Tone, { box: string; icon: string }> = {
    success: { box: 'border-green-200 bg-green-50', icon: '!text-green-600' },
    warning: { box: 'border-amber-200 bg-amber-50', icon: '!text-amber-600' },
    error: { box: 'border-red-200 bg-red-50', icon: '!text-red-600' },
}

interface Summary {
    tone: Tone
    icon: string
    title: string
    body: string
}

const summarize = ({
    isLive,
    meetsAllRequirements,
    requirements,
}: GoLiveRequirementsResponse): Summary => {
    if (isLive) {
        return {
            tone: 'success',
            icon: 'check_circle',
            title: 'This profile is live',
            body: 'Nothing to do here.',
        }
    }

    if (meetsAllRequirements) {
        return {
            tone: 'success',
            icon: 'rocket_launch',
            title: 'Ready to go live',
            // The automatic sweep is daily, so nothing happens on its own before midnight.
            body: 'Every requirement is met. The automatic check only runs at midnight, so take the profile live below.',
        }
    }

    const open = requirements.filter((requirement) => !requirement.met).length

    return {
        tone: 'warning',
        icon: 'error',
        title: `${open} ${open === 1 ? 'requirement' : 'requirements'} still open`,
        body: 'The profile stays offline until everything below is ticked off.',
    }
}

const Banner = ({ tone, icon, title, body }: Summary) => (
    <div
        className={clsx(
            'flex items-start gap-3 rounded-lg border border-solid p-3',
            TONE_CLASSES[tone].box,
        )}>
        <Icon name={icon} fontSize={20} theme="filled" classNameIcon={TONE_CLASSES[tone].icon} />
        <div>
            <p className="font-semibold text-neutral-900">{title}</p>
            <p className="text-md text-neutral-600">{body}</p>
        </div>
    </div>
)

const ErrorMessage = ({ children }: { children: string }) => (
    <div
        className={clsx(
            'flex items-start gap-3 rounded-lg border border-solid p-3',
            TONE_CLASSES.error.box,
        )}>
        <Icon name="warning" fontSize={20} theme="filled" classNameIcon={TONE_CLASSES.error.icon} />
        <p className="text-md text-red-700">{children}</p>
    </div>
)

const OWNER_CLASSES: Record<GoLiveRequirementOwner, string> = {
    provider: 'bg-primary-50 text-primary-800',
    sales: 'bg-amber-50 text-amber-700',
    trustoo: 'bg-neutral-100 text-neutral-600',
}

const RequirementRow = ({
    requirement,
    action,
}: {
    requirement: GoLiveRequirement
    action?: ReactNode
}) => (
    <li className="flex items-start gap-3 bg-white p-3">
        <span
            className={clsx(
                'mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                requirement.met ? 'bg-green-100' : 'bg-red-100',
            )}>
            <Icon
                name={requirement.met ? 'check' : 'close'}
                fontSize={14}
                classNameIcon={requirement.met ? '!text-green-700' : '!text-red-700'}
            />
        </span>
        <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
                <p className="text-md font-medium text-neutral-900">{requirement.label}</p>
                {!requirement.met && (
                    <span
                        className={clsx(
                            'shrink-0 rounded-full px-2 py-px text-sm font-medium',
                            OWNER_CLASSES[requirement.owner],
                        )}>
                        {requirement.ownerLabel}
                    </span>
                )}
            </div>
            {requirement.detail && (
                <p
                    className={clsx(
                        'text-md',
                        requirement.met ? 'text-neutral-500' : 'text-red-700',
                    )}>
                    {requirement.detail}
                </p>
            )}
            {action}
        </div>
    </li>
)

const GoLiveRequirementsModal = NiceModal.create(({ providerId }: GoLiveRequirementsModalProps) => {
    const modal = useModal()
    const dispatch = useAppDispatch()
    const { data, isLoading, error, reload } = useGoLiveRequirements(providerId)
    const [isEnabling, setIsEnabling] = useState(false)
    const [isRecalculating, setIsRecalculating] = useState(false)
    const [actionError, setActionError] = useState<string | null>(null)
    const canEdit = isSuperUser() || hasAccess('providers', 'w')
    const requirements = data?.requirements ?? []
    const metCount = requirements.filter((requirement) => requirement.met).length

    const onClose = () => {
        modal.resolve()
        modal.hide()
    }

    const onEnable = () => {
        setIsEnabling(true)
        setActionError(null)

        enableProfile(providerId)
            .then(() => {
                dispatch(fetchProvider(providerId, true))
                onClose()
            })
            .catch((enableFailure: unknown) => {
                setActionError(toEnableProfileError(enableFailure))
                // The gate refused, so show what changed since the modal was opened.
                reload()
            })
            .finally(() => setIsEnabling(false))
    }

    const onRecalculateScore = () => {
        setIsRecalculating(true)
        setActionError(null)

        recalculateScore(providerId)
            .then(() => reload())
            .catch((failure: unknown) => setActionError(toRecalculateScoreError(failure)))
            .finally(() => setIsRecalculating(false))
    }

    // The score is the one blocker an admin can clear from here: it is stored, and nothing
    // recalculates an offline provider until someone saves the profile.
    const rowAction = (requirement: GoLiveRequirement): ReactNode =>
        requirement.key === 'score' && !requirement.met && canEdit ? (
            <button
                type="button"
                onClick={onRecalculateScore}
                disabled={isRecalculating}
                className="mt-2 inline-flex cursor-pointer items-center gap-1 rounded-full border border-solid border-neutral-300 bg-white px-2 py-1 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50 disabled:cursor-default disabled:text-neutral-400">
                <Icon name="refresh" fontSize={14} classNameIcon="!text-neutral-500" />
                {isRecalculating ? 'Recalculating' : 'Recalculate score'}
            </button>
        ) : null

    return (
        <BaseModal onClose={onClose} maxWidth={560}>
            <BaseModalHeader>
                <h2 className="text-xl font-semibold text-neutral-900">Go-live requirements</h2>
            </BaseModalHeader>
            <BaseModalMainContent className="space-y-4">
                {isLoading && (
                    <div className="py-6">
                        <LinearLoader />
                    </div>
                )}
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {data && (
                    <>
                        <Banner {...summarize(data)} />
                        <div>
                            <div className="flex items-baseline justify-between">
                                <span className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
                                    Checklist
                                </span>
                                <span className="text-md font-medium text-neutral-700">
                                    {metCount} of {requirements.length} met
                                </span>
                            </div>
                            <div className="mt-2 h-1 overflow-hidden rounded-full bg-neutral-200">
                                <div
                                    className="h-full rounded-full bg-green-500 transition-all duration-300"
                                    style={{
                                        width: `${(metCount / Math.max(requirements.length, 1)) * 100}%`,
                                    }}
                                />
                            </div>
                            <ul className="mt-3 divide-y divide-solid divide-neutral-200 overflow-hidden rounded-lg border border-solid border-neutral-200">
                                {requirements.map((requirement) => (
                                    <RequirementRow
                                        key={requirement.key}
                                        requirement={requirement}
                                        action={rowAction(requirement)}
                                    />
                                ))}
                            </ul>
                        </div>
                    </>
                )}
                {actionError && <ErrorMessage>{actionError}</ErrorMessage>}
            </BaseModalMainContent>
            <BaseModalFooter>
                <div className="row justify-end gap-2">
                    <Button appearance="outlined_primary" onClick={onClose}>
                        Close
                    </Button>
                    {canEdit && data && !data.isLive && (
                        <Button
                            appearance="primary"
                            disabled={!data.meetsAllRequirements || isEnabling}
                            onClick={onEnable}>
                            {isEnabling ? <LoadingDots /> : 'Take profile live'}
                        </Button>
                    )}
                </div>
            </BaseModalFooter>
        </BaseModal>
    )
})

export default GoLiveRequirementsModal
